import { forwardRef } from 'react'
import { formatDateOnly, parseDateOnly } from '../../../utils/dateUtils'
import './AccountsReceivablePdfCanvas.css'

const COMPANY_NAME = 'Pró Médico Ltda'
const COMPANY_FOOTER = 'Pró Médico Ltda — Material Médico-Hospitalar | Pelotas/RS'

const FILTER_STATUS_LABELS = {
  pending: 'A vencer',
  overdue: 'Atrasado',
  paid: 'Pago',
  cancelled: 'Cancelado'
}

/** Status exibido no PDF conforme vencimento (igual à lógica da listagem). */
function resolveDisplayStatus(row) {
  const status = row.status || 'pending'

  if (status === 'paid') {
    return { key: 'paid', label: 'Pago' }
  }
  if (status === 'cancelled') {
    return { key: 'cancelled', label: 'Cancelado' }
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = parseDateOnly(row.due_date)
  const isOverdue = Boolean(due) && due < today

  if (status === 'overdue' || isOverdue) {
    return { key: 'overdue', label: 'Atrasado' }
  }

  return { key: 'upcoming', label: 'A vencer' }
}

function formatCurrency(value) {
  const num = parseFloat(value) || 0
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(num)
}

function formatDate(dateString) {
  return formatDateOnly(dateString)
}

function getInstallmentLabel(row) {
  const text = row.description || row.observation || ''
  const match = String(text).match(/Parcela\s+(\d+\/\d+)/i)
  if (match) return match[1]
  return '1/1'
}

function resolveCustomerLabel(accounts, filters) {
  const selectedNames = Array.isArray(filters?.selectedCustomerNames)
    ? filters.selectedCustomerNames.filter(Boolean)
    : []

  if (selectedNames.length === 1) return selectedNames[0]
  if (selectedNames.length > 1) return `${selectedNames.length} clientes`

  const names = [...new Set(
    accounts
      .map((a) => a.customer_name)
      .filter(Boolean)
  )]

  if (names.length === 1) return names[0]
  if (filters?.search?.trim()) return `Filtro: "${filters.search.trim()}"`
  if (names.length > 1) return `${names.length} clientes`
  return 'Contas a receber'
}

function buildFilterSummary(filters) {
  const parts = []
  if (Array.isArray(filters?.status) && filters.status.length) {
    parts.push(`Status: ${filters.status.map((s) => FILTER_STATUS_LABELS[s] || s).join(', ')}`)
  }

  const selectedNames = Array.isArray(filters?.selectedCustomerNames)
    ? filters.selectedCustomerNames.filter(Boolean)
    : []
  if (selectedNames.length === 1) {
    parts.push(`Cliente: ${selectedNames[0]}`)
  } else if (selectedNames.length > 1) {
    parts.push(`${selectedNames.length} clientes`)
  } else {
    parts.push('Todos os clientes')
  }

  if (filters?.dueDateFrom && filters?.dueDateTo) {
    parts.push(`Período: ${formatDate(filters.dueDateFrom)} a ${formatDate(filters.dueDateTo)}`)
  } else if (filters?.nextDays) {
    parts.push(`Próximos ${filters.nextDays} dias`)
  } else if (filters?.lastDays) {
    parts.push(`Últimos ${filters.lastDays} dias`)
  }
  return parts.join(' · ') || 'Conforme filtros aplicados na listagem'
}

const AccountsReceivablePdfCanvas = forwardRef(function AccountsReceivablePdfCanvas(
  { accounts = [], filters = {} },
  ref
) {
  const totalAmount = accounts.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0)
  const customerLabel = resolveCustomerLabel(accounts, filters)
  const uniqueCustomerNames = [...new Set(accounts.map((a) => a.customer_name).filter(Boolean))]
  const singleCustomer = uniqueCustomerNames.length === 1
    ? accounts.find((a) => a.customer_name === uniqueCustomerNames[0])
    : null
  const issuedAt = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })

  return (
    <div ref={ref} className="ar-pdf-canvas">
      <header className="ar-pdf-header">
        <div className="ar-pdf-brand">
          <div className="ar-pdf-logo">PM</div>
          <div>
            <p className="ar-pdf-company-name">{COMPANY_NAME}</p>
            <p className="ar-pdf-company-sub">Relatório financeiro</p>
          </div>
        </div>
        <div className="ar-pdf-title-block">
          <h1 className="ar-pdf-title">Contas a Receber</h1>
          <p className="ar-pdf-meta">Emissão: {issuedAt}</p>
          <p className="ar-pdf-meta">{accounts.length} título(s)</p>
        </div>
      </header>

      <div className="ar-pdf-body">
        <div className="ar-pdf-parties">
          <div>
            <p className="ar-pdf-party-label">Cliente / Destinatário</p>
            <p className="ar-pdf-party-name">{customerLabel}</p>
            {singleCustomer?.customer_cpf_cnpj && (
              <p className="ar-pdf-party-detail">
                CPF/CNPJ: {singleCustomer.customer_cpf_cnpj}
              </p>
            )}
          </div>
          <div>
            <p className="ar-pdf-party-label">Filtros aplicados</p>
            <p className="ar-pdf-party-detail">{buildFilterSummary(filters)}</p>
          </div>
        </div>

        <table className="ar-pdf-table">
          <thead>
            <tr>
              <th>Vencimento</th>
              <th>Cliente</th>
              <th>Descrição</th>
              <th className="center">Parcela</th>
              <th className="center">Status</th>
              <th className="right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((row) => {
              const displayStatus = resolveDisplayStatus(row)
              return (
                <tr key={row.id}>
                  <td>{formatDate(row.due_date)}</td>
                  <td>{row.customer_name || row.user_name || '-'}</td>
                  <td>
                    <p className="ar-pdf-desc-main">
                      {row.description || row.observation || `Título #${row.id}`}
                    </p>
                    {row.number && (
                      <p className="ar-pdf-desc-sub">Nº {row.number}</p>
                    )}
                  </td>
                  <td className="center">{getInstallmentLabel(row)}</td>
                  <td className="center">
                    <span className={`ar-pdf-status ${displayStatus.key}`}>
                      {displayStatus.label}
                    </span>
                  </td>
                  <td className="right">{formatCurrency(row.amount)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <div className="ar-pdf-summary">
          <div className="ar-pdf-total-box">
            <div className="ar-pdf-total-row">
              <span>Quantidade de títulos</span>
              <strong>{accounts.length}</strong>
            </div>
            <div className="ar-pdf-total-final">
              <p className="ar-pdf-total-final-label">Valor total</p>
              <p className="ar-pdf-total-final-value">{formatCurrency(totalAmount)}</p>
            </div>
          </div>
        </div>

        <footer className="ar-pdf-footer">
          {COMPANY_FOOTER}
        </footer>
      </div>
    </div>
  )
})

export default AccountsReceivablePdfCanvas
